import { inject, Injectable } from '@angular/core';
import { catchError, defer, map, switchMap, take, throwError, type Observable } from 'rxjs';

import { ModulesService } from '../../../api/services/modules.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { composeDeclineMessage, openFlags } from './review-flags.util';
import type { ReviewFlag } from './review-flags.types';

const MAX_WRITE_ATTEMPTS = 3;
const FLAGS_KEY = 'flags';
const FLAG_COUNT_KEY = 'flagCount';
const DECLINE_MESSAGE_KEY = 'declineMessage';

export interface ReviewFlagSave {
  moduleId: string;
  entryId: string;
  fieldKey: string;
  /** Server value the local edit started from. */
  baseOwnValue: Record<string, unknown>;
  /** Locally desired values; keys not present here are untouched. */
  desiredOwnValue: Record<string, unknown>;
}

export interface SavedReviewFlagWrite {
  entry: Entry;
  ownValue: Record<string, unknown>;
}

/** ETag-aware persistence for review flags with an operation-level, per-target rebase. */
@Injectable({ providedIn: 'root' })
export class ReviewFlagPersistenceService {
  readonly #entriesService = inject(EntriesService);
  readonly #modulesService = inject(ModulesService);

  save(write: ReviewFlagSave): Observable<SavedReviewFlagWrite> {
    return defer(() => this.#attempt(write, 1));
  }

  #attempt(write: ReviewFlagSave, attempt: number): Observable<SavedReviewFlagWrite> {
    return this.#entriesService.getEntry(write.moduleId, write.entryId).pipe(
      take(1),
      switchMap(current => {
        const ownValue = this.#rebaseOwnValue(write, current);
        const etag = current._etag ?? current.etag;
        const rebased: Entry = {
          ...current,
          uiState: undefined,
          ...(etag ? { _etag: etag } : {}),
          entry: {
            archived: current.entry?.archived ?? false,
            ...(current.entry ?? {}),
            [write.fieldKey]: ownValue,
          },
        };

        return this.#modulesService
          .updateModuleEntry({ moduleId: write.moduleId, entryId: write.entryId, body: rebased })
          .pipe(
            map(response => {
              const saved = (response ?? rebased) as Entry;
              const savedOwnValue = this.#readOwnValue(saved, write.fieldKey);
              return { entry: saved, ownValue: Object.keys(savedOwnValue).length > 0 ? savedOwnValue : ownValue };
            }),
            catchError(error => {
              if (this.#isConflict(error) && attempt < MAX_WRITE_ATTEMPTS) {
                return this.#attempt(write, attempt + 1);
              }
              return throwError(() => error);
            })
          );
      })
    );
  }

  #rebaseOwnValue(write: ReviewFlagSave, current: Entry): Record<string, unknown> {
    const rebased = this.#readOwnValue(current, write.fieldKey);
    for (const [key, value] of Object.entries(write.desiredOwnValue)) {
      if (key === FLAGS_KEY) continue;
      if (value === undefined) delete rebased[key];
      else rebased[key] = value;
    }

    if (FLAGS_KEY in write.desiredOwnValue) {
      const flags = this.#rebaseFlags(
        this.#flags(write.baseOwnValue[FLAGS_KEY]),
        this.#flags(write.desiredOwnValue[FLAGS_KEY]),
        this.#flags(rebased[FLAGS_KEY])
      );
      rebased[FLAGS_KEY] = flags;
      rebased[FLAG_COUNT_KEY] = openFlags(flags).length;
      // A locked review deliberately omits declineMessage: the reviewer may already have edited
      // the final rejection text, and a late flag-resolution save must not compose over it.
      // When the key is part of the local intent, compose from the MERGED flags so concurrent
      // other-target edits are represented too.
      if (DECLINE_MESSAGE_KEY in write.desiredOwnValue) {
        rebased[DECLINE_MESSAGE_KEY] = composeDeclineMessage(flags);
      }
    }
    return rebased;
  }

  #rebaseFlags(base: readonly ReviewFlag[], desired: readonly ReviewFlag[], current: readonly ReviewFlag[]): ReviewFlag[] {
    const result = new Map(current.map(flag => [flag.target, flag]));
    const baseByTarget = new Map(base.map(flag => [flag.target, flag]));
    const desiredByTarget = new Map(desired.map(flag => [flag.target, flag]));
    const localTargets = new Set([...baseByTarget.keys(), ...desiredByTarget.keys()]);

    for (const target of localTargets) {
      const before = baseByTarget.get(target);
      const after = desiredByTarget.get(target);
      if (this.#sameFlag(before, after)) continue;
      if (after) result.set(target, after);
      else result.delete(target);
    }
    return [...result.values()];
  }

  #sameFlag(left: ReviewFlag | undefined, right: ReviewFlag | undefined): boolean {
    return (
      left?.target === right?.target &&
      left?.label === right?.label &&
      left?.reason === right?.reason &&
      !!left?.resolved === !!right?.resolved
    );
  }

  #flags(value: unknown): ReviewFlag[] {
    if (!Array.isArray(value)) return [];
    return value.filter(
      (candidate): candidate is ReviewFlag =>
        !!candidate &&
        typeof candidate === 'object' &&
        typeof candidate.target === 'string' &&
        typeof candidate.label === 'string' &&
        typeof candidate.reason === 'string'
    );
  }

  #readOwnValue(entry: Entry, fieldKey: string): Record<string, unknown> {
    const value = entry.entry?.[fieldKey];
    return value && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, unknown>) } : {};
  }

  #isConflict(error: unknown): boolean {
    const status = (error as { status?: number } | null)?.status;
    return status === 409 || status === 412;
  }
}
